import { useState, useEffect, useCallback } from "react";
import { unifarmFactory } from "../../../utils/contract";
import { GET_MY_STAKES } from "../../../api/queries";
import { apiRequest } from "../../../api/apiService";
import { useChainId } from "wagmi";
import { ApiUrl } from "../../../utils/ApiUrl";

export const useMyStakes = (stakerAddress, limit = 10, offset = 0, searchQuery = '', sortValue = 'blockTimestamp@desc') => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(0);
    const chainId = useChainId();

    const fetchMyStakes = useCallback(async () => {
        if (!stakerAddress) {
            setData([]);
            setTotal(0);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Parse sort value
            const [orderBy, orderDirection] = sortValue.split('@');

            // Build where filter
            let whereFilter;

            // Add search filter - multi-field OR search
            if (searchQuery && searchQuery.trim() !== '') {
                const search = searchQuery.trim().toLowerCase();

                // Structure: OR at root level, each condition includes staker
                whereFilter = {
                    or: [
                        { staker: stakerAddress, contract_contains: search },
                        { staker: stakerAddress, id_contains: search },
                        { staker: stakerAddress, transactionHash_contains: search }
                    ]
                };
            } else {
                // No search, just filter by staker
                whereFilter = {
                    staker: stakerAddress,
                };
            }

            // Step 1: Fetch total count for pagination
            const countResponse = await apiRequest({
                url: unifarmFactory.rootTokens,
                method: "POST",
                data: {
                    query: GET_MY_STAKES,
                    variables: {
                        where: whereFilter,
                        first: 1000, // Get approximate total
                        skip: 0,
                        orderBy: orderBy,
                        orderDirection: orderDirection
                    },
                },
                auth: false,
            });

            const totalStakes = countResponse?.data?.stakeds?.length || 0;
            setTotal(totalStakes);

            // Step 2: Fetch paginated stakes from the graph with sorting
            const response = await apiRequest({
                url: unifarmFactory.rootTokens,
                method: "POST",
                data: {
                    query: GET_MY_STAKES,
                    variables: {
                        where: whereFilter,
                        first: limit,
                        skip: offset,
                        orderBy: orderBy,
                        orderDirection: orderDirection
                    },
                },
                auth: false,
            });

            const stakes = response?.data?.stakeds ?? [];

            // Step 3: Fetch pool details for each stake via API
            const stakesWithMetadata = await Promise.all(
                stakes.map(async (stake) => {
                    try {
                        // Fetch pool details from API
                        const poolResponse = await apiRequest({
                            url: ApiUrl + `/pool/child-pool/${stake.contract}`,
                            method: "GET",
                            auth: true,
                        });

                        const poolData = poolResponse?.data;

                        // Return stake with enriched metadata
                        return {
                            ...stake,
                            metadata: {
                                name: poolData?.createdBy?.name || null,
                                imageUrl: poolData?.createdBy?.imageUrl || null,
                                description: poolData?.poolDescription || poolData?.createdBy?.description || null,
                                tagline: poolData?.tagline || null,
                                email: poolData?.createdBy?.email || null,
                                twitter: poolData?.createdBy?.socialLinks?.twitter || null,
                                instagram: poolData?.createdBy?.socialLinks?.instagram || null,
                                discord: poolData?.createdBy?.socialLinks?.discord || null,
                                createdAt: poolData?.createdAt || null,
                            },
                            poolDetails: {
                                rootPool: poolData?.rootPool || null,
                                creatorFeePercent: poolData?.creatorFeePercent || null,
                                stakingAmount: poolData?.stakingAmount || null,
                                apyPercent: poolData?.apyPercent || null,
                                childContractHash: poolData?.childContractHash || null,
                            }
                        };
                    } catch (metadataError) {
                        console.error(`Error fetching pool details for contract ${stake.contract}:`, metadataError);
                        // Return stake without metadata if fetch fails
                        return {
                            ...stake,
                            metadata: null,
                            poolDetails: null
                        };
                    }
                })
            );

            setData(stakesWithMetadata);
        } catch (err) {
            console.error("Error fetching my stakes:", err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [stakerAddress, chainId, limit, offset, searchQuery, sortValue]);

    useEffect(() => {
        fetchMyStakes();
    }, [fetchMyStakes]);

    return {
        data,
        loading,
        error,
        total,
        refetch: fetchMyStakes
    };
};